// TIP-609 — the allowance card, all eight product states.
//
// Layout as drawn: counters left, actions right, vertically centred, with a
// 1px rule between counters. The top rule is orange at rest and #F0A93C while
// blocked, in BOTH blocking states — the guideline calls the monthly state
// coral but only its banner and counter are (inconsistency 29).
//
// Counter rules from the rules box:
//   Claimed → two counters. 20 a month, max 5 in any week.
//   Member  → ONE counter. 30 a month, no weekly limit. The week counter is not
//             drawn at all — absent from the DOM, not zero and not greyed out.
//   plus a third green bought counter whenever that balance is above zero.
import React from "react";
import "./counterpartyBriefs.css";
import CounterStrip from "./CounterStrip";
import CounterDivider from "./CounterDivider";
import BlockingBanner from "./BlockingBanner";
import CbPanel from "./CbPanel";
import CbButton from "./CbButton";
import CbPill from "./CbPill";
import { CARD_COPY, PAGE_COPY } from "./briefsFixtures";

export const BusinessRow = ({ name, pill, note = PAGE_COPY.sharingNote }) => (
  <div className="cb-business-row">
    <div className="cb-business-id">
      <h2 className="cb-business-name">{name}</h2>
      {pill}
    </div>
    {note && <span className="cb-sharing-note">{note}</span>}
  </div>
);

const AllowanceCard = ({ allowance, business, onOrder, onBuy, onBecomeMember }) => {
  const { week, month, bought, banner, blocked, isMember, hasBought, monthHeldByWeek, weekAtCap, showMembershipCta } = allowance;

  const memberButton = showMembershipCta && (
    <CbButton variant="secondary" onClick={onBecomeMember}>{CARD_COPY.becomeMember}</CbButton>
  );

  return (
    <>
      <BusinessRow
        name={business.name}
        pill={
          <CbPill
            testId="cb-tier-pill"
            variant={isMember ? "member" : "claimed"}
            label={isMember ? PAGE_COPY.tierPill.member : PAGE_COPY.tierPill.claimed}
          />
        }
      />

      <CbPanel data-testid="cb-allowance-card" variant="card" blocked={blocked}>
        <div className="cb-card-row">
          <div className="cb-counters">
            {/* Not drawn at all for a Member. */}
            {week && (
              <>
                <CounterStrip
                  testId="cb-counter-week"
                  eyebrow={CARD_COPY.weekEyebrow}
                  value={week.remaining}
                  tail={CARD_COPY.weekTail(week.cap)}
                  note={weekAtCap ? CARD_COPY.weekNoteCapped(allowance.weekResetDate) : CARD_COPY.weekNote(allowance.weekResetDate)}
                  tone={blocked && weekAtCap ? "week-capped" : undefined}
                />
                <CounterDivider />
              </>
            )}

            <CounterStrip
              testId="cb-counter-month"
              eyebrow={CARD_COPY.monthEyebrow}
              value={month.remaining}
              tail={CARD_COPY.monthTail(month.allowance)}
              note={monthHeldByWeek ? CARD_COPY.monthNoteWeekCapped : CARD_COPY.monthNote(allowance.resetDate)}
              tone={blocked && month.remaining === 0 ? "month-capped" : undefined}
            />

            {hasBought && (
              <>
                <CounterDivider />
                <CounterStrip
                  testId="cb-counter-bought"
                  eyebrow={CARD_COPY.boughtEyebrow}
                  value={bought}
                  tail={CARD_COPY.boughtTail}
                  note={CARD_COPY.boughtNote}
                  tone="bought"
                />
              </>
            )}
          </div>

          {/* The design flips the order when blocked: the primary sits last
              once there is nothing to order. Reproduced. */}
          <div className="cb-actions">
            {blocked ? (
              <>
                <CbButton onClick={onBuy}>{CARD_COPY.buyOne}</CbButton>
                {memberButton}
              </>
            ) : (
              <>
                {memberButton}
                <CbButton onClick={onOrder}>{CARD_COPY.order}</CbButton>
              </>
            )}
          </div>
        </div>
      </CbPanel>

      <BlockingBanner banner={banner} allowance={allowance} onBuy={onBuy} onBecomeMember={onBecomeMember} />
    </>
  );
};

export default AllowanceCard;
